import { FC } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { CustomChartPoint } from '@/entities/dashboards';
import { EmptyState } from '../../parts/EmptyState';
import { ChartCardShell } from '../ChartCardShell';
import { ChartPointTooltip } from './ChartPointTooltip';
import { CHART_AXIS_TICK, CHART_LINE_COLOR, CHART_SERIES_COLOR, formatAxisValue } from './chartAxis';

type Props = {
  title: string;
  points: CustomChartPoint[];
};

/** Ширина оси названий: столько же, сколько отведено подписи строки в остальных диаграммах. */
const LABEL_AXIS_WIDTH = 140;

/** Высота строки одного столбца и место под ось величины — из них складывается высота диаграммы. */
const BAR_ROW_HEIGHT = 28;
const VALUE_AXIS_HEIGHT = 32;

/**
 * Столбчатая диаграмма: величина по категориям.
 *
 * Столбцы горизонтальные: названия категорий приходят из выборки и бывают длинными, а под
 * вертикальными столбцами такая подпись либо повёрнута, либо обрезана.
 *
 * Высота диаграммы растёт по числу категорий, а не подгоняется под карточку: у полутора десятков
 * категорий подогнанные столбцы слились бы в заливку. Не поместившееся прокручивается карточкой.
 */
export const BarChartView: FC<Props> = ({ title, points }) => {
  if (points.length === 0) {
    return (
      <ChartCardShell title={title}>
        <EmptyState />
      </ChartCardShell>
    );
  }

  return (
    <ChartCardShell title={title}>
      <ResponsiveContainer width="100%" height={points.length * BAR_ROW_HEIGHT + VALUE_AXIS_HEIGHT}>
        <BarChart data={points} layout="vertical">
          <CartesianGrid stroke={CHART_LINE_COLOR} strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatAxisValue}
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: CHART_LINE_COLOR }}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={LABEL_AXIS_WIDTH}
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip content={<ChartPointTooltip />} cursor={{ fill: CHART_LINE_COLOR }} />
          <Bar dataKey="value" fill={CHART_SERIES_COLOR} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCardShell>
  );
};
